import { debounce } from 'lodash-es';
import { poll, recordCustomEventToNewRelic } from './helpers';
import type { BranchError, CustomLinkData } from 'branch-sdk';
import { InitialUrlSearchParams } from './environment';

const APP_STORE_REPLACED_ATTRIBUTE = 'data-appstore-replaced';
const BRANCH_DEEPLINK_FAILURE_COOLDOWN_MS = 1 * 60 * 1000;
const BRANCH_DEEPLINK_CACHE_MAX_ENTRIES = 20;

type BranchDeeplinkCacheEntry = {
  promise?: Promise<string>;
  retryAfter?: number;
};

class BranchDeeplinkCooldownError extends Error {
  constructor() {
    super('Branch deeplink generation is cooling down after a recent failure');
    this.name = 'BranchDeeplinkCooldownError';
  }
}

const branchDeeplinkCache = new Map<string, BranchDeeplinkCacheEntry>();

// Function to wait for Branch SDK to be ready with polling
export const isBranchSDKInitAndReady = async (timeout: number = 10000, interval: number = 100): Promise<boolean> => {
  try {
    return Boolean(await poll(() => !!(window.branch && typeof window.branch.link === 'function'), timeout, interval));
  } catch (error) {
    console.log('Branch SDK time out. ', error);
    recordCustomEventToNewRelic('branch_sdk_error', {
      timestamp: Date.now(),
      message: `Branch SDK time out. Failed to start Mutation Observer ${error}`,
    });
    return false;
  }
};

export const generateBranchDeeplink = (linkData: BranchLinkData): Promise<string> => {
  return new Promise((resolve, reject) => {
    const branchInstance = window.branch;
    branchInstance.link(linkData, (error: BranchError, link: string | null) => {
      if (error) {
        recordCustomEventToNewRelic('branch_sdk_error', {
          timestamp: Date.now(),
          message: `Branch deeplink generation error. ${error}`,
          linkData: JSON.stringify(linkData),
        });
        reject(new Error(error));
      } else if (link) {
        resolve(link);
      } else {
        recordCustomEventToNewRelic('branch_sdk_error', {
          timestamp: Date.now(),
          message: `Branch deeplink generation error. No link generated`,
          linkData: JSON.stringify(linkData),
        });
        reject(new Error('No link generated'));
      }
    });
  });
};

// Helper function to create branch link data with common options
const createBranchLinkData = (data: Record<string, CustomLinkData>, options?: BranchLinkOptions): BranchLinkData => {
  return {
    feature: options?.feature || '',
    channel: options?.channel || '',
    campaign: options?.campaign || '',
    data,
  };
};

const parseUrlForPageInfo = (pathname: string) => {
  const pathSegments = pathname.split('/').filter(Boolean);

  const map: Record<string, string> = {
    pages: 'page',
    products: 'product',
    collections: 'collection',
    index: 'homepage',
    search: 'search',
  };

  const getPageHandleForSearch = () => {
    const urlParams = InitialUrlSearchParams;
    const searchQuery = urlParams.get('q') || 'products';
    return `search-${searchQuery.replace(/[^a-zA-Z0-9]/g, '-').toLowerCase()}`;
  };

  const pageType = pathSegments.length === 0 ? 'index' : pathSegments[0] || 'pages';
  const pageHandle = pathSegments.length === 0 ? 'home' : pathSegments[1] || 'unknown';

  return {
    pageType: map[pageType] || 'page',
    pageHandle: pageType === 'search' ? getPageHandleForSearch() : pageHandle,
  };
};

// Helper to create link data for generic pages
const createGenericPageData = (pageType: string, pathname: string) => {
  const pageTitle = document.title || `Check out this ${pageType === 'homepage' ? 'page' : pageType} on ALO`;
  const searchParams = window.location.search;
  const data: Record<string, CustomLinkData> = {
    $og_title: pageTitle,
    $desktop_url: window.location.href,
    $canonical_url: window.location.href,
  };

  // Add query params for non-search pages
  if (searchParams && pageType !== 'search') {
    data.custom_data_query_params = searchParams;
  }

  // Add search-specific data
  if (pageType === 'search' && searchParams) {
    const URLparams = InitialUrlSearchParams;
    data.custom_data_search_query = URLparams.get('q') || '';
    data.custom_data_original_search_url = `${pathname}${searchParams}`;
  }

  return data;
};

const getBranchDeeplinkCacheKey = (pathname: string, options?: BranchLinkOptions) =>
  JSON.stringify({
    href: window.location.href,
    title: document.title || '',
    pathname,
    campaign: options?.campaign || '',
    channel: options?.channel || '',
    feature: options?.feature || '',
  });

const pruneBranchDeeplinkCache = () => {
  while (branchDeeplinkCache.size > BRANCH_DEEPLINK_CACHE_MAX_ENTRIES) {
    const oldestKey = branchDeeplinkCache.keys().next().value;
    if (!oldestKey) return;
    branchDeeplinkCache.delete(oldestKey);
  }
};

const createDynamicDeeplink = async (pathname: string, options?: BranchLinkOptions): Promise<string> => {
  // Parse URL to determine page type and handle
  const { pageType, pageHandle } = parseUrlForPageInfo(pathname);
  const currentUrl = window.location.href;

  // For products and collections, use their specific deeplink generators
  if (pageType === 'product') {
    const data: Record<string, CustomLinkData> = {
      $og_title: document.title || 'Check out this product',
      product_id: pageHandle,
      $desktop_url: currentUrl,
      $canonical_url: currentUrl,
    };

    const linkData = createBranchLinkData(data, options);
    return generateBranchDeeplink(linkData);
  } else if (pageType === 'collection') {
    const data: Record<string, CustomLinkData> = {
      $og_title: document.title || 'Check out this collection',
      collection_handle: pageHandle,
      $desktop_url: currentUrl,
      $canonical_url: currentUrl,
    };

    const linkData = createBranchLinkData(data, options);
    return generateBranchDeeplink(linkData);
  }

  // For all other page types (search, pages, homepage, etc.), create generic deeplink
  const data = createGenericPageData(pageType, pathname);
  const linkData = createBranchLinkData(data, options);
  return generateBranchDeeplink(linkData);
};

export const generateDynamicDeeplink = async (pathname: string, options?: BranchLinkOptions): Promise<string> => {
  const cacheKey = getBranchDeeplinkCacheKey(pathname, options);
  const cachedEntry = branchDeeplinkCache.get(cacheKey);

  if (cachedEntry?.retryAfter && cachedEntry.retryAfter > Date.now()) {
    throw new BranchDeeplinkCooldownError();
  }

  if (cachedEntry?.promise) {
    return cachedEntry.promise;
  }

  const promise = createDynamicDeeplink(pathname, options).catch((error) => {
    branchDeeplinkCache.set(cacheKey, {
      retryAfter: Date.now() + BRANCH_DEEPLINK_FAILURE_COOLDOWN_MS,
    });
    throw error;
  });

  branchDeeplinkCache.set(cacheKey, { promise });
  pruneBranchDeeplinkCache();
  return promise;
};

export const generateCurrentPageDeeplink = async () => {
  const urlParams = InitialUrlSearchParams;
  const utmSource = urlParams.get('utm_source');
  const utmMedium = urlParams.get('utm_medium');
  const utmCampaign = urlParams.get('utm_campaign');

  return generateDynamicDeeplink(window.location.pathname, {
    channel: utmSource || undefined,
    feature: utmMedium || undefined,
    campaign: utmCampaign || undefined,
  });
};

const hasAppStoreUrl = (element: Element, branchAppStoreUrl: string) =>
  Boolean(
    element.getAttribute('onclick')?.includes(branchAppStoreUrl) ||
      element.getAttribute('href')?.includes(branchAppStoreUrl)
  );

const replaceAppStoreUrl = (element: Element, branchAppStoreUrl: string, deeplink: string) => {
  const href = element.getAttribute('href');
  const onclick = element.getAttribute('onclick');

  if (element.tagName.toLowerCase() === 'a' || href?.includes(branchAppStoreUrl)) {
    element.setAttribute('href', deeplink);
  }

  if (onclick?.includes(branchAppStoreUrl)) {
    element.setAttribute('onclick', `location.href=${JSON.stringify(deeplink)}`);
  }

  // shouldn't be needed other than for visual QA in dom; can be removed
  element.setAttribute(APP_STORE_REPLACED_ATTRIBUTE, 'true');
};

export const attachBranchDeeplinkButtons = async (branchAppStoreUrl: string) => {
  // Find all elements containing the App Store URL (in href, onclick from banner, or other attributes)
  const appStoreLinks = document.querySelectorAll(`[onclick*="${branchAppStoreUrl}"], [href*="${branchAppStoreUrl}"]`);
  const unreplacedAppStoreLinks = Array.from(appStoreLinks).filter((element) =>
    hasAppStoreUrl(element, branchAppStoreUrl)
  );

  if (unreplacedAppStoreLinks.length === 0) {
    return;
  }

  try {
    const deeplink = await generateCurrentPageDeeplink();

    unreplacedAppStoreLinks.forEach((element) => replaceAppStoreUrl(element, branchAppStoreUrl, deeplink));
  } catch (error) {
    if (error instanceof BranchDeeplinkCooldownError) {
      return;
    }

    recordCustomEventToNewRelic('branch_sdk_error', {
      timestamp: Date.now(),
      message: `Branch deeplink target replacement failed. ${
        error instanceof Error ? error.message : JSON.stringify(error)
      }`,
      targetCount: unreplacedAppStoreLinks.length,
      branchAppStoreUrl,
    });
  }
};

export async function observeBranchDeeplinkTargets(branchAppStoreUrl: string) {
  // Wait for Branch SDK to be ready before starting
  const isSDKReady = await isBranchSDKInitAndReady();
  if (!isSDKReady) {
    console.warn('Branch SDK not ready, skipping deeplink observation');
    return;
  }

  // Process any existing elements on page load
  await attachBranchDeeplinkButtons(branchAppStoreUrl);

  // Debounced function to process mutations
  const debouncedProcessMutations = debounce(async () => {
    await attachBranchDeeplinkButtons(branchAppStoreUrl);
  }, 300);

  // Create observer for the entire document
  const observer = new MutationObserver((mutations) => {
    // Check if any mutations contain relevant content
    // some/most of this check likely be consolidated with the logic in attachBranchDeeplinkButtons in a future refactor, but for now this helps avoid calling the debounced function unnecessarily
    const hasRelevantContent = mutations.some((mutation) => {
      if (mutation.type === 'childList' && mutation.addedNodes.length > 0) {
        return Array.from(mutation.addedNodes).some((node) => {
          if (node.nodeType === Node.ELEMENT_NODE) {
            const element = node as Element;
            return (
              element.querySelector &&
              (element.querySelector(`[onclick*="${branchAppStoreUrl}"]`) ||
                element.querySelector(`[href*="${branchAppStoreUrl}"]`) ||
                element.matches(`[onclick*="${branchAppStoreUrl}"]`) ||
                element.matches(`[href*="${branchAppStoreUrl}"]`))
            );
          }
          return false;
        });
      }
      return false;
    });

    if (hasRelevantContent) {
      debouncedProcessMutations();
    }
  });

  // Observe the entire document
  observer.observe(document.body, {
    childList: true,
    subtree: true,
    attributes: false, // We don't need attribute changes
    characterData: false, // We don't need text changes
  });

  return observer;
}

interface BranchLinkOptions {
  campaign?: string;
  channel?: string;
  feature?: string;
}
